import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButton } from "@angular/material/button";
import { MatCard, MatCardContent, MatCardHeader, MatCardTitle } from "@angular/material/card";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatInput } from "@angular/material/input";
import { FuseAlertComponent } from "../../../../@fuse/components/alert";
import { EntrySelectorComponent } from "../../../layout/common/selectors/entry-selector.component";
import { AdmissionMotif } from "../../../model/admission/admission-motif.model";
import { JoinQueueRequest } from "../../../model/queue/join-queue-request.model";
import { TicketDTO } from "../../../model/queue/ticketDTO.model";
import { AdmissionMotifsService } from "../../../services/admission-motifs.service";
import { QueueService } from "../../../services/queue.service";
import { Subject, takeUntil } from "rxjs";
import { EstablishmentBranding, EstablishmentParamService } from "../../../services/establishment-param.service";

@Component({
    selector: 'join-queue-tablet',
    templateUrl: './join-queue-tablet.component.html',
    styleUrls: ['./join-queue-tablet.component.css'],
    imports: [
        MatCardContent,
        MatCardHeader,
        MatCardTitle,
        MatCard,
        MatFormField,
        ReactiveFormsModule,
        FuseAlertComponent,
        MatInput,
        MatLabel,
        EntrySelectorComponent,
        MatButton,
        MatIcon,
    ]
})
export class JoinQueueTabletComponent implements OnInit, OnDestroy {
    queueForm: FormGroup;
    currentTicket: TicketDTO | null = null;
    motifs: AdmissionMotif[] = [];
    successMessage = '';
    errorMessage = '';
    isSubmitting = false;
    branding: EstablishmentBranding;

    private readonly _unsubscribeAll: Subject<any> = new Subject<any>();

    private readonly pageShowHandler = (event: PageTransitionEvent): void => {
        if (event.persisted) {
            this.resetFlow();
        }
    };

    constructor(
        private fb: FormBuilder,
        private admissionMotifService: AdmissionMotifsService,
        private queueService: QueueService,
        private establishmentParamService: EstablishmentParamService
    ) {
        this.branding = this.establishmentParamService.branding;
        this.queueForm = this.fb.group({
            firstName: ['', [Validators.required]],
            lastName: ['', [Validators.required]],
            motif: ['', [Validators.required]]
        });
    }

    ngOnInit(): void {
        this.loadMotifs();
        this.establishmentParamService.branding$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((branding: EstablishmentBranding) => {
                this.branding = branding;
            });
        this.establishmentParamService.load().subscribe();
        window.addEventListener('pageshow', this.pageShowHandler);
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        window.removeEventListener('pageshow', this.pageShowHandler);
    }

    joinQueue(): void {
        if (this.queueForm.invalid || this.isSubmitting) {
            this.queueForm.markAllAsTouched();
            return;
        }

        this.isSubmitting = true;
        this.successMessage = '';
        this.errorMessage = '';

        const newQueueRequest: JoinQueueRequest = {
            firstName: this.queueForm.value.firstName,
            lastName: this.queueForm.value.lastName,
            deviceId: this.generateEphemeralDeviceId(),
            admissionMotifId: JSON.parse(this.queueForm.value.motif).id
        };

        this.queueService.join(newQueueRequest).subscribe({
            next: (data) => {
                this.currentTicket = data;
                this.successMessage = 'Enregistrement effectué.';
                this.isSubmitting = false;
                window.scrollTo({ top: 0, behavior: 'smooth' });
            },
            error: () => {
                this.errorMessage = "L'enregistrement a échoué. Veuillez réessayer.";
                this.isSubmitting = false;
            }
        });
    }

    registerAnotherPerson(): void {
        this.resetFlow();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    private loadMotifs(): void {
        this.admissionMotifService.getAll(0, 10, '', 'name', 'asc', true).subscribe((data: any) => {
            if (Array.isArray(data)) {
                this.motifs = data;
            } else if (data && data.content) {
                this.motifs = data.content;
            } else {
                this.motifs = [];
            }
        });
    }

    private generateEphemeralDeviceId(): string {
        return self.crypto.randomUUID();
    }

    private resetFlow(): void {
        this.currentTicket = null;
        this.successMessage = '';
        this.errorMessage = '';
        this.isSubmitting = false;
        this.queueForm.reset({
            firstName: '',
            lastName: '',
            motif: ''
        });
        this.queueForm.markAsPristine();
        this.queueForm.markAsUntouched();
    }
}
